import React from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Breadcrumbs,
  Link,
} from "@mui/material";
import { NavigateNext } from "@mui/icons-material";
import { borderRadius, shadows } from "../utils/designSystem";

interface PageLayoutProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: Array<{ label: string; href?: string }>;
  children: React.ReactNode;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  showPaper?: boolean;
  paperProps?: any;
  headerActions?: React.ReactNode;
}

export const PageLayout = ({
  title,
  subtitle,
  breadcrumbs,
  children,
  maxWidth = "lg",
  showPaper = false,
  paperProps = {},
  headerActions,
}: PageLayoutProps) => (
  <Container maxWidth={maxWidth} sx={{ py: 4 }}>
    {/* Breadcrumbs */}
    {breadcrumbs && breadcrumbs.length > 0 && (
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs
          separator={<NavigateNext fontSize="small" />}
          aria-label="breadcrumb"
        >
          {breadcrumbs.map((crumb, index) => {
            if (index === breadcrumbs.length - 1) {
              return (
                <Typography key={index} color="text.primary" variant="body2">
                  {crumb.label}
                </Typography>
              );
            }
            return (
              <Link
                key={index}
                color="inherit"
                href={crumb.href}
                variant="body2"
                sx={{
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                {crumb.label}
              </Link>
            );
          })}
        </Breadcrumbs>
      </Box>
    )}

    {/* Page Header */}
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography
          variant="h3"
          component="h1"
          sx={{
            mb: subtitle ? 1 : 3,
            fontWeight: 700,
            color: "text.primary",
          }}
        >
          {title}
        </Typography>
        {headerActions && <Box sx={{ ml: 2 }}>{headerActions}</Box>}
      </Box>
      {subtitle && (
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{
            fontWeight: 400,
            maxWidth: "600px",
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>

    {/* Page Content */}
    {showPaper ? (
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: borderRadius.lg,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          boxShadow: shadows.sm,
          ...paperProps,
        }}
      >
        {children}
      </Paper>
    ) : (
      <Box>{children}</Box>
    )}
  </Container>
);
